import React, { useEffect, useState } from 'react'
import { API_BASE_URL } from '../config/api'
import axios from 'axios'
import { Dialog } from 'primereact/dialog'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Tag } from 'primereact/tag'
import { Timeline } from 'primereact/timeline'
import { Card } from 'primereact/card'
import { Member, Payment, PaymentType } from 'shared'

interface PaymentHistoryModalProps {
  visible: boolean
  onHide: () => void
  member: Member | null
}

const PaymentHistoryModal: React.FC<PaymentHistoryModalProps> = ({ visible, onHide, member }) => {
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (visible && member) {
      fetchPaymentHistory()
    }
  }, [visible, member])

  const fetchPaymentHistory = async () => {
    if (!member) return

    try {
      setLoading(true)
      const response = await axios.get(
        `http://localhost:3000/api/members/${member.id}/payment-history`
      )
      setPayments(response.data.data?.payments || [])
    } catch (error) {
      console.error('Error fetching payment history:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const amountBodyTemplate = (payment: Payment) => {
    return <span className="font-semibold">{formatCurrency(Number(payment.amount))}</span>
  }

  const typeBodyTemplate = (payment: Payment) => {
    const getColor = (type: PaymentType) => {
      switch (type) {
        case PaymentType.MEMBERSHIP_FEE: return 'info'
        case PaymentType.DONATION: return 'success'
        case PaymentType.EVENT_FEE: return 'warning'
        default: return 'secondary'
      }
    }

    const getLabel = (type: PaymentType) => {
      switch (type) {
        case PaymentType.MEMBERSHIP_FEE: return 'Membership'
        case PaymentType.DONATION: return 'Donation'
        case PaymentType.EVENT_FEE: return 'Event'
        case PaymentType.OTHER: return 'Other'
        default: return type
      }
    }

    return <Tag value={getLabel(payment.paymentType)} severity={getColor(payment.paymentType)} />
  }

  // Calculate year-by-year summary
  const yearSummary = React.useMemo(() => {
    const summary = new Map<number, { count: number; total: number }>()

    payments.forEach(payment => {
      if (payment.paymentYear) {
        const existing = summary.get(payment.paymentYear) || { count: 0, total: 0 }
        summary.set(payment.paymentYear, {
          count: existing.count + 1,
          total: existing.total + Number(payment.amount)
        })
      }
    })

    return Array.from(summary.entries())
      .sort((a, b) => b[0] - a[0]) // Sort by year descending
      .map(([year, data]) => ({ year, ...data }))
  }, [payments])

  const totalPaid = React.useMemo(() => {
    return payments.reduce((sum, p) => sum + Number(p.amount), 0)
  }, [payments])

  return (
    <Dialog
      header={`Payment History - ${member?.firstName} ${member?.lastName}`}
      visible={visible}
      style={{ width: 'min(95vw, 900px)' }}
      onHide={onHide}
      maximizable
    >
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-blue-50">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Total Payments</div>
              <div className="text-2xl font-bold text-blue-600">{payments.length}</div>
            </div>
          </Card>
          <Card className="bg-green-50">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Total Paid</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</div>
            </div>
          </Card>
          <Card className="bg-purple-50">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Years Paid</div>
              <div className="text-2xl font-bold text-purple-600">{yearSummary.length}</div>
            </div>
          </Card>
        </div>

        {/* Year-by-Year Breakdown */}
        {yearSummary.length > 0 && (
          <Card>
            <h4 className="text-lg font-semibold mb-3">Payment by Year</h4>
            <div className="space-y-2">
              {yearSummary.map(({ year, count, total }) => (
                <div key={year} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="font-medium">{year}</span>
                  <div className="text-right">
                    <span className="font-bold text-blue-600">{formatCurrency(total)}</span>
                    <span className="text-sm text-gray-500 ml-2">({count} payment{count > 1 ? 's' : ''})</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Payment History Table */}
        <Card>
          <h4 className="text-lg font-semibold mb-3">Payment Details</h4>
          <DataTable
            value={payments}
            loading={loading}
            paginator
            rows={10}
            className="p-datatable-sm"
            emptyMessage="No payments found for this member"
            showGridlines
            stripedRows
          >
            <Column
              field="receiveDate"
              header="Date"
              body={(payment) => formatDate(payment.receiveDate)}
              sortable
              style={{ minWidth: '120px' }}
            />
            <Column
              field="amount"
              header="Amount"
              body={amountBodyTemplate}
              sortable
              style={{ minWidth: '100px' }}
            />
            <Column
              field="paymentType"
              header="Type"
              body={typeBodyTemplate}
              sortable
              style={{ minWidth: '120px' }}
            />
            <Column
              field="paymentYear"
              header="Year"
              body={(payment) => payment.paymentYear || '-'}
              sortable
              style={{ minWidth: '80px' }}
            />
            <Column
              field="paymentMethod"
              header="Method"
              body={(payment) => payment.paymentMethod}
              style={{ minWidth: '120px' }}
            />
            <Column
              field="reference"
              header="Reference"
              body={(payment) => (
                <span className="text-xs font-mono text-gray-600">
                  {payment.reference || '-'}
                </span>
              )}
              style={{ minWidth: '150px' }}
            />
          </DataTable>
        </Card>
      </div>
    </Dialog>
  )
}

export default PaymentHistoryModal
